"use client";

import { useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { ArrowUpRight, ZoomIn } from "lucide-react";
import GalleryAmazonLink from "@/components/gallery/GalleryAmazonLink";
import { buildGalleryDressSearchTerms } from "@/lib/designer/amazonSearch";
import { Button } from "@/components/ui/button";
import { PhotoModal } from "@/components/ui/photo-modal";
import type { GalleryDress } from "@/types/gallery";
import { getGalleryPhotoImages } from "./galleryPhotoImages";

interface GalleryCardWithPreviewProps {
  dress: GalleryDress;
  detailHref: string;
  heroImage: {
    url: string | null;
    alt: string;
  };
  itemName: string;
  itemCategory: string;
  absoluteIndex: number;
  priceLabel?: string;
  priority?: boolean;
}

export default function GalleryCardWithPreview({
  dress,
  detailHref,
  heroImage,
  itemName,
  itemCategory,
  absoluteIndex,
  priceLabel,
  priority = false,
}: GalleryCardWithPreviewProps) {
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);
  const photoImages = getGalleryPhotoImages(dress, heroImage);

  return (
    <article className="relative min-w-0">
      <Link
        href={detailHref}
        prefetch={false}
        aria-label={`View ${itemName} details`}
        className="group block min-h-0 rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background"
        data-gallery-ticket-id={dress.id}
        data-gallery-item-name={itemName}
        data-gallery-item-category={itemCategory}
        data-gallery-item-index={absoluteIndex}
        data-gallery-source="card"
      >
        <div className="relative aspect-3/4 overflow-hidden rounded-xl bg-muted">
          {heroImage.url ? (
            <Image
              src={heroImage.url}
              alt={heroImage.alt}
              fill
              className="object-cover text-transparent"
              sizes="(max-width: 767px) 46vw, (max-width: 1023px) 30vw, (max-width: 1279px) 25vw, 320px"
              priority={priority}
              fetchPriority={priority ? "high" : "auto"}
              quality={75}
            />
          ) : (
            <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
              No image
            </div>
          )}
        </div>
        <h2 className="mt-2.5 line-clamp-2 min-h-10 text-sm font-medium leading-5 text-foreground group-hover:underline">
          {itemName}
        </h2>
      </Link>

      {/* Same destination as the card link above: kept as a click target
          (analytics source "build") but out of the tab order and a11y tree
          so each card is one stop for keyboard and screen-reader users. */}
      <Button
        asChild
        variant="primary"
        size="sm"
        className="mt-2 h-auto min-h-14 w-full gap-2 rounded-lg px-2 py-2 text-center text-xs leading-snug whitespace-normal shadow-none sm:text-sm"
      >
        <Link
          href={detailHref}
          prefetch={false}
          tabIndex={-1}
          aria-hidden="true"
          data-gallery-ticket-id={dress.id}
          data-gallery-item-name={itemName}
          data-gallery-item-category={itemCategory}
          data-gallery-item-index={absoluteIndex}
          data-gallery-source="build"
        >
          <span>
            {priceLabel ? "Build this dress" : "See dress details"}
            {priceLabel ? (
              <span className="mt-0.5 block font-normal">
                From {priceLabel}
              </span>
            ) : null}
          </span>
          <ArrowUpRight className="size-3.5 shrink-0" aria-hidden />
        </Link>
      </Button>

      <GalleryAmazonLink
        searchTerms={buildGalleryDressSearchTerms(dress)}
        placement="gallery_grid"
      />

      {photoImages.length > 0 && (
        <button
          type="button"
          onClick={() => setIsPreviewOpen(true)}
          className="group/preview absolute right-1 top-1 z-10 flex size-11 items-center justify-center rounded-full text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 sm:right-2 sm:top-2"
          aria-label={`Preview ${itemName} photos`}
        >
          <span className="flex size-8 items-center justify-center rounded-full border border-border bg-background/90 transition-colors group-hover/preview:bg-background">
            <ZoomIn className="size-3.5" aria-hidden />
          </span>
        </button>
      )}

      <PhotoModal
        open={isPreviewOpen}
        onOpenChange={setIsPreviewOpen}
        images={photoImages}
        title={itemName}
      />
    </article>
  );
}
